import * as Api from '@ViewModels';
import { ResourceType } from './types';
import {
	campaignSessionStore,
	CampaignResource,
	CampaignSession,
	createAIContextFromEmailData,
	createAIContextFromSocialData,
	createAIContextFromBlogData,
} from './campaignSessionStore';

// Initialize session store on first load
if (typeof window !== 'undefined') {
	const globalWindow = window as typeof window & { __levitate_campaign_session_initialized__?: boolean };
	if (!globalWindow.__levitate_campaign_session_initialized__) {
		globalWindow.__levitate_campaign_session_initialized__ = true;
		campaignSessionStore.clearSession();
	}
}

const canUpdateExistingSession = (existingSession: CampaignSession, primaryType: ResourceType): boolean => {
	return existingSession.primaryResource.type === primaryType;
};

const tryUpdateExistingSession = (
	primaryType: ResourceType,
	content: Api.AIContext,
	scheduledDate?: Date
): string | null => {
	const existingSession = campaignSessionStore.currentSession;
	if (!existingSession) return null;

	if (canUpdateExistingSession(existingSession, primaryType)) {
		updateSession(primaryType, content, scheduledDate);
		return existingSession.id;
	}

	const updatedSession = updateSession(primaryType, content, scheduledDate);
	return updatedSession?.id || null;
};

export const startSession = (primaryType: ResourceType, content: Api.AIContext, scheduledDate?: Date): string => {
	const existingSession = campaignSessionStore.currentSession;

	if (existingSession) {
		const sessionId = tryUpdateExistingSession(primaryType, content, scheduledDate);
		if (sessionId) {
			return sessionId;
		}
		console.warn(
			`Failed to update existing session with ${primaryType}, returning existing session ID to prevent data loss`
		);
		return existingSession.id;
	}

	return campaignSessionStore.startSession(primaryType, content, scheduledDate);
};

export const getSession = (sessionId?: string): CampaignSession | null => campaignSessionStore.getSession(sessionId);

export const updateSession = (
	resourceType: ResourceType,
	content: Api.AIContext,
	scheduledDate?: Date
): CampaignSession | null => campaignSessionStore.updateSession(resourceType, content, scheduledDate);

export interface SessionProgressUpdate {
	session: CampaignSession | null;
	remainingResources: ResourceType[];
	isCampaignComplete: boolean;
}

export const updateSessionProgress = (
	resourceType: ResourceType,
	content: Api.AIContext,
	scheduledDate?: Date
): SessionProgressUpdate => {
	const session = campaignSessionStore.updateSession(resourceType, content, scheduledDate);

	if (!session) {
		return {
			session: null,
			remainingResources: [],
			isCampaignComplete: false,
		};
	}

	const remainingResources = getIncompleteResources(session);
	return {
		session,
		remainingResources,
		isCampaignComplete: remainingResources.length === 0,
	};
};

export const clearSession = (): void => campaignSessionStore.clearSession();

export const clearExpiredSessions = (): void => campaignSessionStore.clearExpiredSessions();

export const hasActiveSession = (): boolean => campaignSessionStore.hasActiveSession;

export const isPrimaryResourceSendNow = (resourceType: ResourceType, isSendNow: boolean): boolean => {
	const session = campaignSessionStore.currentSession;
	if (!session) {
		return isSendNow;
	}
	return session.primaryResource.type === resourceType && isSendNow;
};

export const getIncompleteResources = (session?: CampaignSession): ResourceType[] => {
	if (session) {
		const incomplete: ResourceType[] = [];
		const allTypes: ResourceType[] = ['email', 'social', 'blog'];

		for (const type of allTypes) {
			const res = session.resources[type];
			if (!res || !res.isCompleted) {
				incomplete.push(type);
			}
		}

		return incomplete;
	}

	return campaignSessionStore.incompleteResources;
};

export const getPrimaryResourceContent = (session?: CampaignSession): Api.AIContext | null => {
	if (session) {
		return session.primaryResource.content;
	}

	return campaignSessionStore.getPrimaryResourceContent();
};

export const getCompletedResources = (session?: CampaignSession): CampaignResource[] => {
	if (session) {
		return Object.values(session.resources).filter(resource => resource !== null) as CampaignResource[];
	}

	return campaignSessionStore.completedResources;
};

// Re-export helper functions for convenience
export { createAIContextFromEmailData, createAIContextFromSocialData, createAIContextFromBlogData };

export const CampaignSessionManager = {
	startSession,
	getSession,
	updateSession,
	updateSessionProgress,
	clearSession,
	clearExpiredSessions,
	hasActiveSession,
	isPrimaryResourceSendNow,
	getIncompleteResources,
	getPrimaryResourceContent,
	getCompletedResources,
	createAIContextFromEmailData,
	createAIContextFromSocialData,
	createAIContextFromBlogData,
};
